/**
 * AdminMemoContactDetailPage — admin-only full memo history for one
 * external contact. Reachable from `/admin/memos` → click a contact.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import PageLoader from '../components/PageLoader';
import { usePageHeader } from '../context/PageHeaderContext';

const initials = (n) => ((n || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?');

const AdminMemoContactDetailPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [data, setData] = useState(null);

  usePageHeader({ title: 'Contact Memo History', back: () => navigate('/admin/memos') }, [navigate]);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    if (!isAdmin || !contactId) return;
    try { const r = await api.get(`/memos/admin/contacts/${contactId}`); setData(r.data); }
    catch (e) { setData({ error: e?.response?.data?.detail || 'Failed to load' }); }
  }, [api, contactId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <div className="min-h-screen flex items-center justify-center"><p className="text-sm text-muted-foreground">Admin only.</p></div>;
  if (data === null) return <PageLoader label="Loading memos..." />;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center"><p className="text-sm text-red-600">{data.error}</p></div>;

  const c = data.contact || {};
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="admin-memo-contact-detail">
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <Card className="rounded-2xl">
          <CardContent className="p-4 flex items-center gap-3">
            <Avatar className="h-12 w-12 flex-shrink-0">
              {c.profile_picture ? <AvatarImage src={c.profile_picture} alt="" /> : null}
              <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] font-semibold">{initials(c.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{c.name || '(unnamed)'}</p>
              <p className="text-xs text-muted-foreground truncate">{[c.title, c.company].filter(Boolean).join(' · ')}</p>
              {c.phone && <p className="text-xs text-muted-foreground">📞 {c.phone}</p>}
              {c.email && <p className="text-xs text-muted-foreground">✉ {c.email}</p>}
            </div>
            <span className="text-[11px] text-muted-foreground">{(data.memos || []).length} memos</span>
          </CardContent>
        </Card>

        <div className="space-y-2" data-testid="admin-memo-contact-memos">
          {(data.memos || []).length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No memos for this contact.</p>
          ) : (data.memos || []).map((m) => (
            <div key={m.id} className="p-3 rounded-2xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-semibold text-[#586D52]">{m.owner_name}</p>
                <p className="text-[11px] text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminMemoContactDetailPage;
